import { useEffect, useState, useMemo } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Truck, MapPin, Package, Check, AlertTriangle, Plus, Trash2 } from "lucide-react";

const FIELD = "stc-input w-full bg-black border border-white/15 px-3 py-2 text-[#F4F1EA] font-body text-[14px] focus:border-[#C9A055] focus:outline-none";

export default function ShippingAdmin() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [allVariants, setAllVariants] = useState([]); // [{key, label, type}]

  const load = async () => {
    try {
      const [s, a] = await Promise.all([
        http.get("/admin/shipping/settings"),
        http.get("/artworks"),
      ]);
      setSettings(s.data);
      // Collect unique variant keys (skip digital — those don't ship)
      const map = new Map();
      for (const art of a.data || []) {
        for (const v of art.variants || []) {
          if (!v.active) continue;
          if ((v.type || "").toLowerCase() === "digital") continue;
          if ((v.type || "").toLowerCase() === "upgrade") continue;
          if (!map.has(v.key)) map.set(v.key, { key: v.key, label: v.label || v.key, type: v.type });
        }
      }
      setAllVariants([...map.values()].sort((x, y) => x.label.localeCompare(y.label)));
    } catch {
      toast.error("Failed to load shipping settings");
    }
  };
  useEffect(() => { load(); }, []);

  const setOrigin = (k, v) => setSettings({ ...settings, origin: { ...settings.origin, [k]: v } });
  const setParcel = (k, v) => setSettings({ ...settings, default_parcel: { ...settings.default_parcel, [k]: Number(v) || 0 } });

  const setVariantParcel = (vk, field, value) => {
    const current = settings.variant_parcels?.[vk] || { ...settings.default_parcel };
    setSettings({
      ...settings,
      variant_parcels: {
        ...(settings.variant_parcels || {}),
        [vk]: { ...current, [field]: Number(value) || 0 },
      },
    });
  };

  const addVariantOverride = (vk) => {
    if (!vk) return;
    if (settings.variant_parcels?.[vk]) { toast.error("Override already exists"); return; }
    setSettings({
      ...settings,
      variant_parcels: {
        ...(settings.variant_parcels || {}),
        [vk]: { ...settings.default_parcel },
      },
    });
  };

  const removeVariantOverride = (vk) => {
    const next = { ...(settings.variant_parcels || {}) };
    delete next[vk];
    setSettings({ ...settings, variant_parcels: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      await http.put("/admin/shipping/settings", {
        origin: settings.origin,
        default_parcel: settings.default_parcel,
        variant_parcels: settings.variant_parcels || {},
        default_carrier: settings.default_carrier,
      });
      toast.success("Shipping settings saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="text-[#8a857d] font-body">Loading…</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="shipping-admin">
      <div>
        <div className="font-display text-2xl text-[#F4F1EA]">Shipping & Labels</div>
        <div className="font-body text-[13px] text-[#8a857d] mt-1">
          Connect Shippo to print real USPS labels from any order. Your studio address prints on every label.
        </div>
      </div>

      {/* Status pill */}
      <div className={`border p-4 ${settings.configured ? "border-[#5fc377]/40 bg-[#5fc377]/5" : "border-orange-400/40 bg-orange-400/5"}`} data-testid="shippo-status">
        <div className="flex items-start gap-3">
          {settings.configured ? <Check className="text-[#5fc377] mt-0.5 flex-shrink-0" size={18}/> : <AlertTriangle className="text-orange-400 mt-0.5 flex-shrink-0" size={18}/>}
          <div className="font-body text-[13px] text-[#F4F1EA]">
            {settings.configured ? (
              <>
                Shippo API key detected (<code className="text-[#C9A055]">{settings.api_key_env}</code>). You can fetch live rates & purchase labels.
              </>
            ) : (
              <>
                <strong className="text-orange-300">Shippo API key not set.</strong> Add <code className="text-[#C9A055]">SHIPPO_API_KEY</code> (live) or <code className="text-[#C9A055]">SHIPPO_TEST_API_KEY</code> (test) to your backend environment.
                Get a key at <a className="text-[#C9A055] underline" href="https://apps.goshippo.com/settings/api" target="_blank" rel="noreferrer">apps.goshippo.com/settings/api</a>.
                Once set, you'll need to redeploy production for the key to take effect.
              </>
            )}
          </div>
        </div>
      </div>

      {/* Origin Address */}
      <div className="border border-white/10 bg-[#0a0a0a] p-5 space-y-3">
        <div className="flex items-center gap-2 font-ui text-[10px] tracking-[0.32em] text-[#C9A055]"><MapPin size={12}/> STUDIO ORIGIN ADDRESS</div>
        <p className="font-body text-[12px] text-[#8a857d]">This is the FROM address that prints on every shipping label.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-sm">
            <div className="font-body text-[12px] text-[#8a857d] mb-1">Full name / studio name</div>
            <input className={FIELD} value={settings.origin.name || ""} onChange={(e) => setOrigin("name", e.target.value)} data-testid="ship-origin-name" />
          </label>
          <label className="text-sm">
            <div className="font-body text-[12px] text-[#8a857d] mb-1">Company (optional)</div>
            <input className={FIELD} value={settings.origin.company || ""} onChange={(e) => setOrigin("company", e.target.value)} />
          </label>
        </div>
        <label className="block text-sm">
          <div className="font-body text-[12px] text-[#8a857d] mb-1">Street address</div>
          <input className={FIELD} value={settings.origin.street1 || ""} onChange={(e) => setOrigin("street1", e.target.value)} data-testid="ship-origin-street1" placeholder="123 Studio St"/>
        </label>
        <label className="block text-sm">
          <div className="font-body text-[12px] text-[#8a857d] mb-1">Apt / Suite (optional)</div>
          <input className={FIELD} value={settings.origin.street2 || ""} onChange={(e) => setOrigin("street2", e.target.value)} />
        </label>
        <div className="grid sm:grid-cols-3 gap-3">
          <label className="text-sm">
            <div className="font-body text-[12px] text-[#8a857d] mb-1">City</div>
            <input className={FIELD} value={settings.origin.city || ""} onChange={(e) => setOrigin("city", e.target.value)} data-testid="ship-origin-city" />
          </label>
          <label className="text-sm">
            <div className="font-body text-[12px] text-[#8a857d] mb-1">State</div>
            <input className={FIELD} value={settings.origin.state || ""} onChange={(e) => setOrigin("state", e.target.value)} maxLength={2} data-testid="ship-origin-state" />
          </label>
          <label className="text-sm">
            <div className="font-body text-[12px] text-[#8a857d] mb-1">ZIP</div>
            <input className={FIELD} value={settings.origin.zip || ""} onChange={(e) => setOrigin("zip", e.target.value)} data-testid="ship-origin-zip" />
          </label>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-sm">
            <div className="font-body text-[12px] text-[#8a857d] mb-1">Phone (carrier requires it)</div>
            <input className={FIELD} value={settings.origin.phone || ""} onChange={(e) => setOrigin("phone", e.target.value)} data-testid="ship-origin-phone" placeholder="555-555-5555"/>
          </label>
          <label className="text-sm">
            <div className="font-body text-[12px] text-[#8a857d] mb-1">Country</div>
            <input className={FIELD} value={settings.origin.country || "US"} onChange={(e) => setOrigin("country", e.target.value)} maxLength={2} />
          </label>
        </div>
      </div>

      {/* Default Parcel */}
      <div className="border border-white/10 bg-[#0a0a0a] p-5 space-y-3">
        <div className="flex items-center gap-2 font-ui text-[10px] tracking-[0.32em] text-[#C9A055]"><Package size={12}/> DEFAULT PARCEL DIMENSIONS</div>
        <p className="font-body text-[12px] text-[#8a857d]">Used to quote rates for every order unless overridden per-variant. Most flat prints ship in a ~13×10×1″ rigid mailer at ~8oz.</p>
        <div className="grid sm:grid-cols-4 gap-3">
          <label className="text-sm">
            <div className="font-body text-[12px] text-[#8a857d] mb-1">Length (in)</div>
            <input type="number" className={FIELD} value={settings.default_parcel.length} onChange={(e) => setParcel("length", e.target.value)} data-testid="ship-parcel-l" />
          </label>
          <label className="text-sm">
            <div className="font-body text-[12px] text-[#8a857d] mb-1">Width (in)</div>
            <input type="number" className={FIELD} value={settings.default_parcel.width} onChange={(e) => setParcel("width", e.target.value)} data-testid="ship-parcel-w" />
          </label>
          <label className="text-sm">
            <div className="font-body text-[12px] text-[#8a857d] mb-1">Height (in)</div>
            <input type="number" className={FIELD} value={settings.default_parcel.height} onChange={(e) => setParcel("height", e.target.value)} data-testid="ship-parcel-h" />
          </label>
          <label className="text-sm">
            <div className="font-body text-[12px] text-[#8a857d] mb-1">Weight (oz)</div>
            <input type="number" className={FIELD} value={settings.default_parcel.weight} onChange={(e) => setParcel("weight", e.target.value)} data-testid="ship-parcel-weight" />
          </label>
        </div>
      </div>

      {/* Per-Variant Parcel Overrides */}
      <PerVariantParcels
        variantParcels={settings.variant_parcels || {}}
        defaultParcel={settings.default_parcel}
        allVariants={allVariants}
        onChange={setVariantParcel}
        onAdd={addVariantOverride}
        onRemove={removeVariantOverride}
      />

      {/* Carrier */}
      <div className="border border-white/10 bg-[#0a0a0a] p-5">
        <div className="flex items-center gap-2 font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-2"><Truck size={12}/> DEFAULT CARRIER</div>
        <select className={FIELD + " max-w-xs"} value={settings.default_carrier || "usps"} onChange={(e) => setSettings({ ...settings, default_carrier: e.target.value })} data-testid="ship-carrier-select">
          <option value="usps">USPS</option>
          <option value="ups">UPS</option>
          <option value="fedex">FedEx</option>
          <option value="dhl_express">DHL Express</option>
        </select>
        <p className="font-body text-[11px] text-[#8a857d] mt-2">Rate listings will filter to this carrier first. Falls back to "all rates" if none are returned.</p>
      </div>

      <div className="flex justify-end gap-3">
        <button onClick={save} disabled={saving} data-testid="ship-save-btn" className="px-6 py-2.5 bg-[#C9A055] text-black hover:opacity-90 font-ui text-[10px] tracking-[0.32em] uppercase disabled:opacity-40">
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>
    </div>
  );
}

function PerVariantParcels({ variantParcels, defaultParcel, allVariants, onChange, onAdd, onRemove }) {
  const [adding, setAdding] = useState("");
  const overrideKeys = Object.keys(variantParcels);
  const availableToAdd = useMemo(
    () => allVariants.filter((v) => !overrideKeys.includes(v.key)),
    [allVariants, overrideKeys]
  );
  const labelFor = (key) => (allVariants.find((v) => v.key === key)?.label) || key;

  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-5 space-y-4" data-testid="variant-parcels-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 font-ui text-[10px] tracking-[0.32em] text-[#C9A055]"><Package size={12}/> PER-VARIANT PARCEL OVERRIDES</div>
          <p className="font-body text-[12px] text-[#8a857d] mt-1 max-w-xl">
            Use this for variants that ship differently from your default parcel — e.g. canvases need a bigger box. If a variant isn't overridden, the default parcel above is used. Digital downloads and upgrades are skipped automatically. <span className="text-[#9a9690]">Tip: 16 oz = 1 lb · 32 oz = 2 lb · 48 oz = 3 lb · 64 oz = 4 lb.</span>
          </p>
        </div>
        {availableToAdd.length > 0 && (
          <div className="flex items-center gap-2">
            <select
              value={adding}
              onChange={(e) => setAdding(e.target.value)}
              className={FIELD + " max-w-[200px]"}
              data-testid="variant-parcel-picker"
            >
              <option value="">Pick a variant…</option>
              {availableToAdd.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
            </select>
            <button
              onClick={() => { if (adding) { onAdd(adding); setAdding(""); } }}
              disabled={!adding}
              data-testid="add-variant-parcel-btn"
              className="px-3 py-2 border border-[#C9A055] text-[#C9A055] hover:bg-[#C9A055] hover:text-black font-ui text-[10px] tracking-[0.32em] uppercase disabled:opacity-40 inline-flex items-center gap-1"
            >
              <Plus size={12}/> Add Override
            </button>
          </div>
        )}
      </div>

      {overrideKeys.length === 0 ? (
        <div className="font-body text-[12px] text-[#8a857d] italic py-2 border-t border-white/5 pt-3">
          No overrides yet. {availableToAdd.length === 0 && "All your shippable variants are already overridden, or no shippable variants exist."}
        </div>
      ) : (
        <div className="space-y-3 border-t border-white/5 pt-3">
          {overrideKeys.map((vk) => {
            const p = variantParcels[vk] || defaultParcel;
            return (
              <div key={vk} className="border border-white/10 p-3 bg-black" data-testid={`variant-parcel-${vk}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-ui text-[11px] tracking-[0.22em] text-[#F4F1EA] uppercase">{labelFor(vk)}</div>
                  <button
                    onClick={() => onRemove(vk)}
                    title="Remove override (will fall back to default parcel)"
                    data-testid={`remove-variant-parcel-${vk}`}
                    className="text-[#9a9690] hover:text-[#d97a7a] p-1"
                  >
                    <Trash2 size={14}/>
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <label className="text-sm">
                    <div className="font-body text-[10px] text-[#8a857d] mb-1">Length (in)</div>
                    <input type="number" className={FIELD} value={p.length || ""} onChange={(e) => onChange(vk, "length", e.target.value)} />
                  </label>
                  <label className="text-sm">
                    <div className="font-body text-[10px] text-[#8a857d] mb-1">Width (in)</div>
                    <input type="number" className={FIELD} value={p.width || ""} onChange={(e) => onChange(vk, "width", e.target.value)} />
                  </label>
                  <label className="text-sm">
                    <div className="font-body text-[10px] text-[#8a857d] mb-1">Height (in)</div>
                    <input type="number" className={FIELD} value={p.height || ""} onChange={(e) => onChange(vk, "height", e.target.value)} />
                  </label>
                  <label className="text-sm">
                    <div className="font-body text-[10px] text-[#8a857d] mb-1">Weight (oz)</div>
                    <input type="number" className={FIELD} value={p.weight || ""} onChange={(e) => onChange(vk, "weight", e.target.value)} />
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

